import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Group, LoadingOverlay, Select, Space, Text } from '@mantine/core';
import { getBillingInvoiceGetBillingInvoicesForCompany, postBillingInvoiceCopyAllBillingInvoiceAdjustments } from '@apis/Invoices';
import { BillingInvoice, BillingInvoiceAdjustment } from '@apis/Invoices/model';
import { useDi } from '@root/Services/DI';
import { FormatService } from '@root/Services/FormatService';
import { CompanyContextService } from '@root/Services/Customers/CompanyContext';
import { NotificationService } from '@root/Services/Notification/NotificationService';

export function CopyAllAdjustmentsModal(props: {
    companyId: number;
    currentInvoiceId: number;
    adjustments: BillingInvoiceAdjustment[];
    onClose: (adjustmentsCopied: boolean) => void;
}) {
    const [loading, setLoading] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
    const [selectedInvoice, setSelectedInvoice] = useState<string | null>(null);
    const fmtSvc = useDi(FormatService);
    const companyCtx = useDi(CompanyContextService);
    const notificationSvc = useDi(NotificationService);

    const availableInvoices = useMemo(() => {
        return [...invoices]
            .filter((i) => i.Id !== props.currentInvoiceId && i.Status === 'Draft')
            .sort((a, b) => {
                const dateA = new Date(a.Year!, a.Month! - 1);
                const dateB = new Date(b.Year!, b.Month! - 1);
                return dateB.getTime() - dateA.getTime();
            })
            .map((i) => {
                const adjCount = i.Adjustments?.length ?? 0;
                const adjLabel = adjCount === 1 ? '1 adjustment' : `${adjCount} adjustments`;
                return {
                    label: `${fmtSvc.formatShortMonthYear(new Date(i.Year!, i.Month! - 1))} (${adjLabel})`,
                    value: i.Id!.toString(),
                };
            });
    }, [invoices, props.currentInvoiceId]);

    const cancel = useCallback(() => props.onClose(false), [props.onClose]);

    useEffect(() => {
        loadInvoices();
    }, []);

    const loadInvoices = async () => {
        setLoading(true);
        const response = await getBillingInvoiceGetBillingInvoicesForCompany({ companyId: props.companyId }, companyCtx.getRequestOptions(true));
        setInvoices(response);
        setLoading(false);
    };

    const handleSave = async () => {
        const invoiceId = selectedInvoice ? parseInt(selectedInvoice, 10) : 0;
        const invoiceSelected = invoices.find((x) => x.Id === invoiceId);
        if (!invoiceSelected) {
            return;
        }
        setSubmitted(true);

        const result = await postBillingInvoiceCopyAllBillingInvoiceAdjustments(
            props.adjustments,
            { billingInvoiceId: invoiceId },
            companyCtx.getRequestOptions(true)
        );

        if (result) {
            notificationSvc.notify(
                'Adjustments Saved',
                `Successfully copied all adjustments to ${fmtSvc.formatShortMonthYear(
                    new Date(invoiceSelected.Year!, invoiceSelected.Month! - 1)
                )} invoice`,
                'success',
                null,
                undefined,
                3000
            );
            props.onClose(true);
        } else {
            notificationSvc.notify('Copy Failed', 'There was an issue copying the adjustments.', 'error', null, undefined, 5000);
            setSubmitted(false);
        }
    };

    return (
        <>
            <LoadingOverlay visible={loading} overlayBlur={2} />
            <Box p="m">
                <Text size="sm" color="dimmed" mb="xs">
                    {props.adjustments.length} adjustment{props.adjustments.length !== 1 ? 's' : ''} will be copied as inactive to the selected month.
                </Text>
                <Select
                    data={availableInvoices}
                    label="Select Invoice to Copy To"
                    value={selectedInvoice}
                    onChange={setSelectedInvoice}
                    placeholder="Select Invoice"
                    searchable
                    nothingFound="No invoices found"
                />
            </Box>
            <Space h="md" />
            <Group position="right">
                <Button data-atid="SettingsCancelButton" onClick={cancel} variant="outline">
                    Cancel
                </Button>
                <Button data-atid="CopyAllAdjustmentsButton" onClick={handleSave} variant={!submitted ? 'filled' : 'light'}>
                    {!submitted ? 'Copy All Adjustments' : 'Copying adjustments...'}
                </Button>
            </Group>
        </>
    );
}
